import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Portal from './Portal';
import { sendContactForm, emailMessages } from '../services/emailService';

const ContactForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    betreff: 'Allgemeine Anfrage',
    nachricht: '',
    prioritat: 'Normal',
    datenschutzAkzeptiert: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validierung: Datenschutz muss akzeptiert sein
    if (!formData.datenschutzAkzeptiert) {
      setErrors({ datenschutz: 'Bitte akzeptieren Sie die Datenschutzerklärung' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS Integration - echte E-Mail wird versendet
      await sendContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.telefon,
        message: formData.nachricht,
        subject: formData.betreff,
        service: `Priorität: ${formData.prioritat}`
      });

      setSubmitStatus('success');

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          telefon: '',
          betreff: 'Allgemeine Anfrage',
          nachricht: '',
          prioritat: 'Normal',
          datenschutzAkzeptiert: false
        });
        setSubmitStatus(null);
        setErrors({});
        onClose();
      }, 2500);
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      setSubmitStatus('error');

      // Fehler-Status nach 5 Sekunden zurücksetzen
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <div style={{ pointerEvents: 'auto' }}>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 backdrop-blur-sm contact-form-overlay"
              style={{
                zIndex: 99998,
                background: 'linear-gradient(to right, rgba(0,0,0,0.5), rgba(205,127,50,0.2))'
              }}
            />

            {/* Form Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-[100vw] md:w-[500px] lg:w-[600px] bg-white contact-form-panel overflow-y-auto contact-form-mobile"
              style={{
                zIndex: 99999,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(205,127,50,0.1)'
              }}
            >
              {/* Header */}
              <div
                className="p-4 md:p-6 text-white sticky top-0 z-20 shadow-md"
                style={{
                  background: 'linear-gradient(to right, #CD7F32, #B8751F)'
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)'
                      }}
                    >
                      <svg className="w-5 h-5 md:w-6 md:h-6 icon-unified" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg md:text-xl font-playfair">Kontakt aufnehmen</h2>
                      <p className="text-white/80 text-xs md:text-sm">Persönliche Beratung in 24 Stunden</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.2)'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Contact Options */}
              <div
                className="p-4 md:p-6"
                style={{
                  backgroundColor: 'rgba(192,192,192,0.1)',
                  borderBottom: '1px solid #C0C0C0'
                }}
              >
                <h3 className="text-base md:text-lg text-gray-900 mb-4 font-playfair">Direkter Kontakt</h3>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <a
                    href="tel:+49871408797909"
                    className="flex flex-col items-center p-3 md:p-4 bg-white rounded-xl transition-all group"
                    style={{
                      border: '1px solid #C0C0C0'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#CD7F32';
                      e.currentTarget.style.backgroundColor = 'rgba(205,127,50,0.05)';
                      const icon = e.currentTarget.querySelector('.contact-icon');
                      if (icon) {
                        icon.style.backgroundColor = 'rgba(205,127,50,0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#C0C0C0';
                      e.currentTarget.style.backgroundColor = 'white';
                      const icon = e.currentTarget.querySelector('.contact-icon');
                      if (icon) {
                        icon.style.backgroundColor = 'rgba(192,192,192,0.2)';
                      }
                    }}
                  >
                    <div
                      className="contact-icon w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 transition-colors"
                      style={{
                        backgroundColor: 'rgba(192,192,192,0.2)'
                      }}
                    >
                      <svg className="w-4 h-4 md:w-6 md:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900 text-sm md:text-base font-playfair">Anrufen</span>
                    <span className="text-xs md:text-sm text-gray-600">Sofort verfügbar</span>
                  </a>

                  <a
                    href="mailto:info@ares-wohnbau.de"
                    className="flex flex-col items-center p-3 md:p-4 bg-white rounded-xl transition-all group"
                    style={{
                      border: '1px solid #C0C0C0'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#CD7F32';
                      e.currentTarget.style.backgroundColor = 'rgba(205,127,50,0.05)';
                      const icon = e.currentTarget.querySelector('.contact-icon');
                      if (icon) {
                        icon.style.backgroundColor = 'rgba(205,127,50,0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#C0C0C0';
                      e.currentTarget.style.backgroundColor = 'white';
                      const icon = e.currentTarget.querySelector('.contact-icon');
                      if (icon) {
                        icon.style.backgroundColor = 'rgba(192,192,192,0.2)';
                      }
                    }}
                  >
                    <div
                      className="contact-icon w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 transition-colors"
                      style={{
                        backgroundColor: 'rgba(192,192,192,0.2)'
                      }}
                    >
                      <svg className="w-4 h-4 md:w-6 md:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900 text-sm md:text-base font-playfair">E-Mail</span>
                    <span className="text-xs md:text-sm text-gray-600">Schnelle Antwort</span>
                  </a>
                </div>
              </div>

              {/* Form */}
              <div className="p-4 md:p-6 mobile-padding">
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm text-gray-900 mb-2 font-playfair">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Ihr Name"
                      className="form-input w-full px-3 md:px-4 py-2 md:py-3 rounded-xl transition-all"
                      style={{
                        border: '1px solid #C0C0C0'
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = '#CD7F32';
                        e.target.style.boxShadow = '0 0 0 3px rgba(205, 127, 50, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#C0C0C0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm text-gray-900 mb-2 font-playfair">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="ihre@email.de"
                      className="form-input w-full px-3 md:px-4 py-2 md:py-3 rounded-xl transition-all"
                      style={{
                        border: '1px solid #C0C0C0'
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = '#CD7F32';
                        e.target.style.boxShadow = '0 0 0 3px rgba(205, 127, 50, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#C0C0C0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label htmlFor="telefon" className="block text-sm text-gray-900 mb-2 font-playfair">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleInputChange}
                      placeholder="+49 15123080311"
                      className="form-input w-full px-3 md:px-4 py-2 md:py-3 rounded-xl transition-all"
                      style={{
                        border: '1px solid #C0C0C0'
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = '#CD7F32';
                        e.target.style.boxShadow = '0 0 0 3px rgba(205, 127, 50, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#C0C0C0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Betreff */}
                  <div>
                    <label htmlFor="betreff" className="block text-sm text-gray-900 mb-2 font-playfair">
                      Betreff
                    </label>
                    <select
                      id="betreff"
                      name="betreff"
                      value={formData.betreff}
                      onChange={handleInputChange}
                      className="form-input w-full px-3 md:px-4 py-2 md:py-3 rounded-xl transition-all"
                      style={{
                        border: '1px solid #C0C0C0'
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = '#CD7F32';
                        e.target.style.boxShadow = '0 0 0 3px rgba(205, 127, 50, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#C0C0C0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="Allgemeine Anfrage">Allgemeine Anfrage</option>
                      <option value="Projektentwicklung">Projektentwicklung</option>
                      <option value="Bauberatung">Bauberatung</option>
                      <option value="Immobilienverwaltung">Immobilienverwaltung</option>
                      <option value="Investitionsberatung">Investitionsberatung</option>
                    </select>
                  </div>

                  {/* Nachricht */}
                  <div>
                    <label htmlFor="nachricht" className="block text-sm text-gray-900 mb-2 font-playfair">
                      Nachricht *
                    </label>
                    <textarea
                      id="nachricht"
                      name="nachricht"
                      value={formData.nachricht}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                      className="form-input w-full px-3 md:px-4 py-2 md:py-3 rounded-xl transition-all resize-none"
                      style={{
                        border: '1px solid #C0C0C0'
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = '#CD7F32';
                        e.target.style.boxShadow = '0 0 0 3px rgba(205, 127, 50, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#C0C0C0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Priorität */}
                  <div>
                    <label className="block text-sm text-gray-900 mb-3 font-playfair">
                      Priorität
                    </label>
                    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
                      {['Normal', 'Dringend', 'Sofort'].map((priority) => (
                        <label key={priority} className="flex items-center">
                          <input
                            type="radio"
                            name="prioritat"
                            value={priority}
                            checked={formData.prioritat === priority}
                            onChange={handleInputChange}
                            className="w-4 h-4 border-gray-300"
                            style={{
                              accentColor: '#CD7F32'
                            }}
                          />
                          <span className="ml-2 text-sm text-gray-700 font-playfair">{priority}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Datenschutz Checkbox */}
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="datenschutz"
                        name="datenschutzAkzeptiert"
                        checked={formData.datenschutzAkzeptiert}
                        onChange={handleInputChange}
                        className={`w-4 h-4 text-bronze-600 border-gray-300 rounded focus:ring-bronze-500 mt-1 ${errors.datenschutz ? 'border-red-500' : ''}`}
                        required
                      />
                      <label htmlFor="datenschutz" className="ml-3 text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Ich habe die{' '}
                        <a
                          href="/datenschutz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-bronze-600 hover:text-bronze-700 underline"
                        >
                          Datenschutzerklärung
                        </a>
                        {' '}zur Kenntnis genommen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *
                      </label>
                    </div>
                    {errors.datenschutz && (
                      <p className="text-red-600 text-xs sm:text-sm ml-7">{errors.datenschutz}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white py-3 md:py-4 px-4 md:px-6 rounded-xl transition-all duration-300 transform disabled:cursor-not-allowed flex items-center justify-center font-playfair"
                    style={{
                      background: isSubmitting ? '#9CA3AF' : 'linear-gradient(to right, #CD7F32, #B8751F)',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.background = 'linear-gradient(to right, #B8751F, #A66B1C)';
                        e.target.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.target.style.background = 'linear-gradient(to right, #CD7F32, #B8751F)';
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="loading-spinner mr-3"></div>
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 icon-unified" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Nachricht senden
                      </>
                    )}
                  </button>

                  {/* Success/Error Messages */}
                  <AnimatePresence>
                    {submitStatus === 'success' && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="p-4 bg-green-50 rounded-xl"
                        style={{
                          border: '1px solid #d1fae5'
                        }}
                      >
                        <div className="flex items-start">
                          <svg className="w-4 h-4 md:w-5 md:h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <div>
                            <span className="text-green-800 font-playfair text-sm md:text-base block">Nachricht erfolgreich gesendet!</span>
                            <span className="text-green-700 text-xs md:text-sm font-playfair">Wir melden uns schnellstmöglich bei Ihnen.</span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {submitStatus === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="p-4 bg-red-50 rounded-xl"
                        style={{
                          border: '1px solid #fecaca'
                        }}
                      >
                        <div className="flex items-start">
                          <svg className="w-4 h-4 md:w-5 md:h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <div>
                            <span className="text-red-800 font-playfair text-sm md:text-base block">Fehler beim Senden</span>
                            <span className="text-red-700 text-xs md:text-sm font-playfair">Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.</span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>

                {/* Footer */}
                <div
                  className="mt-6 md:mt-8 pt-4 md:pt-6"
                  style={{
                    borderTop: '1px solid #C0C0C0'
                  }}
                >
                  <div className="flex items-center justify-center space-x-4 md:space-x-8 text-xs md:text-sm text-gray-600">
                    <div className="flex items-center">
                      <svg className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{ color: '#CD7F32' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="font-playfair">100% sicher</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{ color: '#CD7F32' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-playfair">24h Antwortzeit</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{ color: '#CD7F32' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                      <span className="font-playfair">Kostenlos</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default ContactForm;

